import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import moduleRoutes from "../../routes/moduleRouteList";
import mastersRouteList from "../routeList";
import { GoogleOAuthCallbackParams } from "./types";

const CALLBACK_PARAM_LABELS: Record<keyof GoogleOAuthCallbackParams, string> = {
  state: "State",
  code: "Authorization Code",
  scope: "Scope",
  authuser: "Auth User",
  prompt: "Prompt",
  iss: "Issuer",
  error: "Error",
  error_description: "Error Description",
  success: "Success",
  message: "Message",
};

function GoogleOAuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const params = useMemo(() => {
    const parsed: GoogleOAuthCallbackParams = {};
    searchParams.forEach((value, key) => {
      parsed[key as keyof GoogleOAuthCallbackParams] = value;
    });
    return parsed;
  }, [searchParams]);

  const hasError = Boolean(params.error);
  const hasCode = Boolean(params.code);
  const isSuccess = params.success === "true" || (hasCode && !hasError);

  const details = Object.entries(CALLBACK_PARAM_LABELS)
    .map(([key, label]) => {
      const value = params[key as keyof GoogleOAuthCallbackParams];
      if (!value) return null;
      return { key, label, value };
    })
    .filter(Boolean) as { key: string; label: string; value: string }[];

  const handleCreateNewForm = () => {
    navigate(
      `${moduleRoutes.root}${moduleRoutes.mainRoute}/${moduleRoutes.masters}/${mastersRouteList.googleForms}/${mastersRouteList.googleFormsCreate}`,
    );
  };

  return (
    <Box className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Paper className="w-full max-w-2xl p-6">
        <Box className="flex items-center gap-2 mb-2">
          {isSuccess ? (
            <CheckCircleOutlineIcon color="success" />
          ) : (
            <ErrorOutlineIcon color={hasError ? "error" : "info"} />
          )}
          <Typography variant="h5" fontWeight={700}>
            Google OAuth Callback
          </Typography>
        </Box>

        <Typography color="text.secondary" className="mb-4">
          OAuth redirect details received from Google.
        </Typography>

        {isSuccess && (
          <Alert severity="success" className="mb-4">
            Google authentication completed successfully.
          </Alert>
        )}

        {hasError && (
          <Alert severity="error" className="mb-4">
            {params.error_description || params.error || "Authentication failed."}
          </Alert>
        )}

        {!hasError && !hasCode && !params.success && (
          <Alert severity="info" className="mb-4">
            No OAuth parameters were found in the callback URL.
          </Alert>
        )}

        <Divider className="my-4" />

        <Typography variant="subtitle1" fontWeight={600} className="mb-3">
          Callback Details
        </Typography>

        {details.length === 0 ? (
          <Typography color="text.secondary">No parameters to display.</Typography>
        ) : (
          <Box className="flex flex-col gap-3">
            {details.map(({ key, label, value }) => (
              <Box
                key={key}
                className="border rounded-md p-3 bg-white break-all"
              >
                <Box className="flex items-center gap-2 mb-1">
                  <Typography fontWeight={600}>{label}</Typography>
                  <Chip label={key} size="small" variant="outlined" />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Box className="flex justify-end mt-6">
          <Button variant="contained" onClick={handleCreateNewForm}>
            Create New Form
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

export default GoogleOAuthCallback;
